import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  idea:{ bg:'#F9F8F5', color:'#8A8A8A', label:'Idea' },
  approved_topic:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Approved Topic' },
  draft:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Draft' },
  pending_review:{ bg:'#F0F0FF', color:'#5C5FDB', label:'Pending Review' },
  revision_required:{ bg:'#FFF0E0', color:'#D6720B', label:'Revision Required' },
  approved:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  published:{ bg:'#556B2F22', color:'#556B2F', label:'Published' },
};

export default function BrandBlog() {
  const { user } = useAuth();
  const canApprove = ['super_admin','sales_manager'].includes(user?.role);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [googleDocContent, setGoogleDocContent] = useState('');
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [googleDocUrl, setGoogleDocUrl] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const pdfInputRef = React.useRef(null);
  const [form, setForm] = useState({
    title:'', topic:'', target_keyword:'', product_name:'',
    content:'', seo_title:'', meta_description:'',
    planned_pub_date:'', actual_pub_date:'', published_url:'',
    status:'idea', revision_notes:''
  });

  useEffect(()=>{ load(); },[]);

  const load = () => {
    setLoading(true);
    get('/bc/blog').then(d=>setArticles(d.data||[])).finally(()=>setLoading(false));
  };

  const openForm = (item=null) => {
    if (item) {
      setEditing(item);
      setForm({
        title:item.title||'', topic:item.topic||'',
        target_keyword:item.target_keyword||'',
        product_name:item.product_name||'',
        content:item.content||'', seo_title:item.seo_title||'',
        meta_description:item.meta_description||'',
        planned_pub_date:item.planned_pub_date?.split('T')[0]||'',
        actual_pub_date:item.actual_pub_date?.split('T')[0]||'',
        published_url:item.published_url||'',
        status:item.status||'idea', revision_notes:item.revision_notes||''
      });
    } else {
      setEditing(null);
      setForm({title:'',topic:'',target_keyword:'',product_name:'',
        content:'',seo_title:'',meta_description:'',
        planned_pub_date:'',actual_pub_date:'',published_url:'',
        status:'idea',revision_notes:''});
    }
    setShowForm(true);
  };

  const openDetail = async (article) => {
    setSelectedArticle(article);
    setGoogleDocUrl(article.google_doc_url||'');
    setGoogleDocContent('');
    setShowDetail(true);
    if (article.google_doc_url) {
      fetchGoogleDoc(article.id);
    }
  };

  const fetchGoogleDoc = async (id) => {
    setLoadingDoc(true);
    try {
      const result = await get('/bc/blog/'+id+'/google-doc-content');
      setGoogleDocContent(result.content||'');
    } catch(err) {
      setGoogleDocContent('<p style="color:#8A8A8A">Could not load Google Doc. Make sure it is set to "Anyone with link can view".</p>');
    } finally { setLoadingDoc(false); }
  };

  const saveGoogleDocUrl = async (id) => {
    setSavingDoc(true);
    try {
      await put('/bc/blog/'+id+'/google-doc', { google_doc_url: googleDocUrl });
      if (googleDocUrl) fetchGoogleDoc(id);
      load();
    } catch(err) { alert(err.message); }
    finally { setSavingDoc(false); }
  };

  const uploadPdf = async (id) => {
    const file = pdfInputRef.current?.files[0];
    if (!file) return;
    setSavingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('token');
      await fetch('/api/bc/blog/'+id+'/pdf', {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}` },
        body:fd
      });
      load();
      alert('PDF uploaded successfully');
    } catch(err) { alert(err.message); }
    finally { setSavingDoc(false); }
  };

  const save = async () => {
    if (!form.title) { alert('Title required'); return; }
    setSaving(true);
    try {
      if (editing) await put('/bc/blog/'+editing.id, form);
      else await post('/bc/blog', form);
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Blog Management</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Plan, write and publish blog articles</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={()=>openForm()}>+ New Article</button>
      </div>

      {loading ? <div className="spinner"/> : articles.length === 0 ? (
        <div className="empty-state"><h3>No articles yet</h3></div>
      ) : (
        <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#2C1F16'}}>
                {['Title','Keyword','Product','Pub Date','Status',''].map(h=>(
                  <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                    color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articles.map((a,i)=>{
                const st = STATUS_COLORS[a.status]||STATUS_COLORS.idea;
                return (
                  <tr key={a.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#2C1F16'}}>
                      {a.title}
                      {a.revision_notes && <div style={{fontSize:10,color:'#D6720B',marginTop:2}}>✏ {a.revision_notes}</div>}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{a.target_keyword||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{a.product_name||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                      {a.planned_pub_date ? new Date(a.planned_pub_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'}) : '—'}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openDetail(a)}>View</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>openForm(a)}>Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ARTICLE DETAIL MODAL */}
      {showDetail && selectedArticle && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:860,width:'95vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{selectedArticle.title}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDetail(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'82vh',overflowY:'auto'}}>

              {/* Google Doc section */}
              <div style={{marginBottom:24,padding:16,background:'#F9F8F5',borderRadius:10,border:'1px solid #E8E0D4'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                  textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>
                  📄 Google Doc
                </div>
                <div style={{display:'flex',gap:8,marginBottom:12}}>
                  <input className="form-control" value={googleDocUrl}
                    onChange={e=>setGoogleDocUrl(e.target.value)}
                    placeholder="Paste Google Doc URL here..."
                    style={{fontSize:12,flex:1}} />
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>saveGoogleDocUrl(selectedArticle.id)}
                    disabled={savingDoc}>
                    {savingDoc?'Saving...':'Save & Load'}
                  </button>
                </div>
                {selectedArticle.google_doc_url && (
                  <div style={{fontSize:11,color:'#8A8A8A',marginBottom:8}}>
                    ℹ Make sure the doc is set to "Anyone with link can view"
                  </div>
                )}
                {loadingDoc && <div className="spinner"/>}
                {googleDocContent && !loadingDoc && (
                  <div style={{background:'#fff',borderRadius:8,padding:20,
                    border:'1px solid #E8E0D4',lineHeight:1.8,fontSize:14,color:'#2C1F16'}}
                    dangerouslySetInnerHTML={{__html: googleDocContent}} />
                )}
              </div>

              {/* PDF section */}
              <div style={{marginBottom:24,padding:16,background:'#F9F8F5',borderRadius:10,border:'1px solid #E8E0D4'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                  textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>
                  📕 PDF Upload
                </div>
                <div style={{display:'flex',gap:8,alignItems:'center'}}>
                  <input type="file" accept=".pdf" ref={pdfInputRef}
                    className="form-control" style={{fontSize:12,flex:1}} />
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>uploadPdf(selectedArticle.id)}
                    disabled={savingDoc}>
                    {savingDoc?'Uploading...':'Upload PDF'}
                  </button>
                </div>
                {selectedArticle.pdf_filename && (
                  <div style={{marginTop:12}}>
                    <div style={{fontSize:11,color:'#2E6B3A',marginBottom:8}}>
                      ✅ PDF uploaded: <strong>{selectedArticle.pdf_filename}</strong>
                    </div>
                    <iframe
                      src={`/api/bc/blog/${selectedArticle.id}/pdf`}
                      style={{width:'100%',height:500,border:'1px solid #E8E0D4',borderRadius:8}}
                      title="Blog PDF"
                    />
                  </div>
                )}
              </div>

              {/* Written content */}
              {selectedArticle.content && (
                <div style={{padding:16,background:'#F9F8F5',borderRadius:10,border:'1px solid #E8E0D4'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>
                    ✍️ Written Content
                  </div>
                  <div style={{background:'#fff',borderRadius:8,padding:20,
                    border:'1px solid #E8E0D4',lineHeight:1.8,fontSize:14,color:'#2C1F16'}}
                    dangerouslySetInnerHTML={{__html: selectedArticle.content}} />
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowDetail(false)}>Close</button>
              <button className="btn btn-primary btn-sm" onClick={()=>{setShowDetail(false);openForm(selectedArticle);}}>Edit Article</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:680,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{editing?'Edit Article':'New Article'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-control" value={form.title}
                  onChange={e=>setForm({...form,title:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Topic</label>
                  <input className="form-control" value={form.topic}
                    onChange={e=>setForm({...form,topic:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Keyword</label>
                  <input className="form-control" value={form.target_keyword}
                    onChange={e=>setForm({...form,target_keyword:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Related Product</label>
                  <input className="form-control" value={form.product_name}
                    onChange={e=>setForm({...form,product_name:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Content / Draft</label>
                <textarea className="form-control" rows={6} value={form.content}
                  onChange={e=>setForm({...form,content:e.target.value})}
                  placeholder="Write article content here..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SEO Title</label>
                  <input className="form-control" value={form.seo_title}
                    onChange={e=>setForm({...form,seo_title:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Planned Publication</label>
                  <input type="date" className="form-control" value={form.planned_pub_date}
                    onChange={e=>setForm({...form,planned_pub_date:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea className="form-control" rows={2} value={form.meta_description}
                  onChange={e=>setForm({...form,meta_description:e.target.value})} />
              </div>
              {(canApprove || editing) && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status}
                      onChange={e=>setForm({...form,status:e.target.value})}>
                      {Object.entries(STATUS_COLORS).map(([k,v])=>(
                        <option key={k} value={k} disabled={['approved','published'].includes(k)&&!canApprove}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.status==='published' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Actual Publication Date</label>
                        <input type="date" className="form-control" value={form.actual_pub_date}
                          onChange={e=>setForm({...form,actual_pub_date:e.target.value})} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Published URL</label>
                        <input className="form-control" value={form.published_url}
                          onChange={e=>setForm({...form,published_url:e.target.value})} />
                      </div>
                    </>
                  )}
                </div>
              )}
              {form.status==='revision_required' && (
                <div className="form-group">
                  <label className="form-label">Revision Notes</label>
                  <textarea className="form-control" rows={2} value={form.revision_notes}
                    onChange={e=>setForm({...form,revision_notes:e.target.value})} />
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':editing?'Save Changes':'Create Article'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
